import { useMemo, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { AutoHideOnScroll, PageTitle, Table } from '@/components';
import { Button } from '@/components/ui/Button';
import { I18nNamespace } from '@/lib/i18n-namespaces';
import { SortOrder } from '@/types/table';
import { useClampedPage, useTableState, useTableEdit } from '@/hooks';
import { DeleteSourceConfirmationModal } from '@/features/Source/components/DeleteSourceConfirmationModal';
import { useCameraActions, useCameraTableData } from './hooks';
import { getCameraTableColumns } from './components/CameraTableColumns';
import { CamerasActionBar } from './components/CamerasActionBar';
import { CameraTableRow } from './types';

export function CameraTable() {
    const { t } = useTranslation(I18nNamespace.CAMERAS);

    const [scrollContainer, setScrollContainer] =
        useState<HTMLDivElement | null>(null);
    const [search, setSearch] = useState('');

    const tableState = useTableState({
        initialPageSize: 10,
        initialSort: { by: 'name', order: SortOrder.ASC },
    });

    const { onPageChange } = tableState;
    const handleSearchSubmit = useCallback(
        (value: string) => {
            if (value === search) return;
            setSearch(value);
            onPageChange(1);
        },
        [search, onPageChange],
    );

    const { cameras, isLoading, totalCount, refetch } = useCameraTableData({
        page: tableState.page,
        pageSize: tableState.pageSize,
        search,
        sortBy: tableState.sortBy,
        sortOrder: tableState.sortOrder,
    });

    useClampedPage({
        page: tableState.page,
        pageSize: tableState.pageSize,
        totalCount: isLoading ? undefined : totalCount,
        onPageChange: tableState.onPageChange,
    });

    const tableEditState = useTableEdit<CameraTableRow>({
        getRowId: (row) => row.sourceId,
    });

    const {
        handleEdit,
        handleDelete,
        handleRefresh,
        saveChanges,
        isSaving,
        deleteConfirmCamera,
        setDeleteConfirmCamera,
        thumbnailKeys,
    } = useCameraActions(refetch, tableEditState.changes);

    const editedRowIds = useMemo(() => {
        const ids = new Set<string>();
        tableEditState.changes.forEach((change) => ids.add(change.rowId));
        return ids;
    }, [tableEditState.changes]);

    const columns = useMemo(
        () =>
            getCameraTableColumns(
                {
                    onEdit: handleEdit,
                    onDelete: setDeleteConfirmCamera,
                    onRefresh: handleRefresh,
                },
                { editedRowIds, thumbnailKeys, t },
            ),
        [
            handleEdit,
            setDeleteConfirmCamera,
            handleRefresh,
            editedRowIds,
            thumbnailKeys,
            t,
        ],
    );

    const handleSave = useCallback(async () => {
        try {
            await saveChanges();
            tableEditState.discard();
        } catch {
            toast.error(t('table.saveError'));
        }
    }, [saveChanges, tableEditState, t]);

    return (
        <div className="h-full flex flex-col relative">
            <AutoHideOnScroll container={scrollContainer}>
                <CamerasActionBar onSearchSubmit={handleSearchSubmit} />
            </AutoHideOnScroll>

            <div
                ref={setScrollContainer}
                className="flex-1 min-h-0 overflow-y-auto scrollbar-grey-500 px-4 panels:px-8 pb-32"
            >
                <div className="w-full max-w-content mx-auto pt-20 pb-24 text-grey-200">
                    <div className="flex flex-col items-start gap-3 @medium:flex-row @medium:items-center @medium:justify-between @medium:gap-0 mb-6 pt-4">
                        <PageTitle text={t('table.title')} />
                        <div className="flex items-center gap-2">
                            <Button
                                variant="ghost"
                                size="medium"
                                disabled={!tableEditState.isDirty || isSaving}
                                onClick={tableEditState.discard}
                            >
                                {t('table.discard')}
                            </Button>
                            <Button
                                variant="primary"
                                size="medium"
                                disabled={!tableEditState.isDirty || isSaving}
                                onClick={handleSave}
                            >
                                {isSaving
                                    ? t('table.saving')
                                    : t('table.saveChanges')}
                            </Button>
                        </div>
                    </div>
                    <Table
                        data={cameras}
                        columns={columns}
                        isLoading={isLoading}
                        page={tableState.page}
                        pageSize={tableState.pageSize}
                        totalCount={totalCount}
                        sortBy={tableState.sortBy}
                        sortOrder={tableState.sortOrder}
                        onPageChange={tableState.onPageChange}
                        onSortChange={tableState.onSortChange}
                        inlineEditConfig={tableEditState.config}
                    />
                </div>
            </div>

            <DeleteSourceConfirmationModal
                isOpen={!!deleteConfirmCamera}
                onConfirm={() =>
                    deleteConfirmCamera && handleDelete(deleteConfirmCamera)
                }
                onCancel={() => setDeleteConfirmCamera(null)}
            />
        </div>
    );
}
